"use strict";
/**
 *  Evaluator - evaluate strings using javascipt syntax in a sandboxed context
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getDataElementRoot = exports.evaluate = exports.conditionMacro = exports.textMacro = exports.evaluatePrimitive = exports.createContext = void 0;
const moment_1 = __importDefault(require("moment"));
const vlx_types_1 = require("@sundaysky/vlx-types");
const functions_1 = require("./functions");
const dataContext_1 = require("./dataContext");
const logger_1 = require("@sundaysky/logger");
const notEvil = require("notevil");
const log = (0, logger_1.logger)("evaluator");
const latestFunctions = (0, functions_1.functions)("latest");
const DATAELEMENTS = "dataElements";
const regexOuter = /\[\[(.*?)]]/g;
const regexInner = /\[\[(.*?)]]/;
/**
 * Create a complete context object by merging the data fields with all 'builtin' functions
 * @param data
 */
const createContext = (data) => {
    if (!data) {
        data = {};
    }
    // dont do this again if this object is already the result of this function
    if (data.__isContext) {
        return data; //todo joseph - change to symbol
    }
    // merge context elements with functions
    let flatData = data[DATAELEMENTS] || data;
    return Object.assign({}, data, latestFunctions, { __isContext: true }, contextAccessors(flatData));
};
exports.createContext = createContext;
const isFunction = (arg) => {
    return typeof arg === "function";
};
const evaluateRaw = (macro, inputContext) => {
    //if this is a raw data object, add in the built in functions
    const context = (0, exports.createContext)(inputContext);
    const result = notEvil.eval(macro, context);
    if (isFunction(result))
        throw new Error(`macro [[${macro}]] cannot be a function`);
    return result;
};
const isPrimitive = (arg) => {
    const type = typeof arg;
    return arg === null || (type !== "object" && type !== "function");
};
const evaluatePrimitive = (macro, context) => {
    const result = evaluateRaw(macro, context);
    if (!isPrimitive(result)) {
        throw new Error(`macro [[${macro}]] does not evaluate to a simple element`);
    }
    return result;
};
exports.evaluatePrimitive = evaluatePrimitive;
/**
 * Evaluate a templated text with macro elements, based on a context
 * @param str e.g 'Hi [[firstname]]'
 * @param context
 * @returns {string}
 */
const textMacro = (str, context) => {
    return str.replace(regexOuter, (o) => {
        const inner = o.match(regexInner)[1];
        try {
            const result = (0, exports.evaluatePrimitive)(inner, context);
            return (result !== undefined && result !== null && !Number.isNaN(result)) ? result : "";
        }
        catch (e) {
            log.info(inner, "evaluation error - field will resolve to blank", e.toString());
            return "";
        }
    });
};
exports.textMacro = textMacro;
/**
 * safely evaluate an expression and resolve to a boolean.
 * @param str
 * @param context
 * @returns {boolean}
 */
const conditionMacro = (str, context) => {
    try {
        const result = (0, exports.evaluatePrimitive)(str, context);
        return result && result !== "false";
    }
    catch (e) {
        return false; // when conditions can not be evaluated they are considered false
    }
};
exports.conditionMacro = conditionMacro;
/**
 * safely evaluate an expression and resolve to a raw object
 * @param str
 * @param context
 * @returns {Object}
 */
const evaluate = (str, context) => {
    var _a;
    try {
        return (_a = evaluateRaw(str, context)) !== null && _a !== void 0 ? _a : null;
    }
    catch (e) {
        return null;
    }
};
exports.evaluate = evaluate;
const getDataElementRoot = (data) => {
    return (0, dataContext_1.unwrapDataContext)(data);
};
exports.getDataElementRoot = getDataElementRoot;
/**
 * Given object and properties, dive into object according to properties
 * will return undefined for all falsy values except zero
 * avoid using dots in property names...
 * @param obj - an object
 * @param property - string of properties, delimited by dot '.'
 * @returns value or undefined
 * @examples -
 * x = { a: { b: { c: "d" } } }
 * getProperty(x, "a.b.c") // "d"
 * getProperty(x, "a.b") // { c: "d" }
 *
 * y = { a: { b: null } }
 * getProperty(x, "a.b") // undefined (not null)
 * y = { a: { b: false } }
 * getProperty(x, "a.b") // undefined (not false)
 */
const getProperty = (obj, property) => {
    const props = property.split(".");
    return props.reduce((acc, prop) => ((acc && (acc[prop] || acc[prop] === 0)) ? acc[prop] : undefined), obj);
};
/**
 * create all accessor functions which safely access the data elements in the context
 * @param data
 * @returns {{
 *      de: (function(*): T),
 *      numberDe: (function(*=): number),
 *      stringDe: (function(*=): string),
 *      booleanDe: (function(*=): boolean),
 *      urlDe: (function(*=): *),
 *      colorDe: (function(*=): *),
 *      datetimeDe: datetimeDe
 *  }}
 */
const contextAccessors = (data) => {
    const de = (value) => {
        return getProperty(data, value);
    };
    const numberDe = (value) => {
        value = de(value);
        return (value === null) || (Array.isArray(value)) ? Number.NaN : Number(value);
        // all invalid numbers will become NaN
    };
    const stringDe = (value) => {
        value = de(value);
        return typeof value === "string" ? value : (typeof value === "number" ? value.toString() : "");
    };
    const booleanDe = (value) => {
        value = de(value);
        return value === true || value === 1 || (typeof value === "string" && ["true", "yes", "y", "1"].includes(value.toLowerCase()));
    };
    const urlDe = (value) => {
        value = de(value);
        return value;
    };
    const colorDe = (value) => {
        // TODO validate color obj (or string?) use isValidColor from hub? what are fallback return values?
        value = de(value);
        return value;
    };
    const datetimeDe = (value) => {
        value = de(value);
        if (value === null || value === undefined || typeof value === "object" || Array.isArray(value)) {
            return null;
        }
        const dateObject = (0, moment_1.default)(value);
        return dateObject.isValid() ? value : null;
    };
    // metadata accessors
    const metadata = (value) => {
        return getProperty((0, vlx_types_1.getMetadata)(data) || {}, value);
    };
    const deConditionKeys = (value) => {
        const m = metadata(value);
        return Array.isArray(m) ? m.map(c => c.key) : m ? [m.key] : [];
    };
    return {
        de,
        numberDe,
        stringDe,
        booleanDe,
        urlDe,
        colorDe,
        datetimeDe,
        deConditionKeys
    };
};
//# sourceMappingURL=evaluator.js.map